import { useMemo, useState } from "react";
import {
	VStack,
	HStack,
	Text,
	Link,
	Button,
	useDisclosure,
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerOverlay,
	Box,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import ConsultantMiniProfile from "@components/createOrder/ConsultantMiniProfile";
import { makeArrayUniqueByURL } from "@utils/utils";
import Chevron from "@components/icons/Chevron";
import { appTheme } from "@styles/Theme";
import Modal from "@components/Modal";

interface Props {
	consultant: {
		id: string;
		firstName?: string;
		lastName?: string;
		hasWorked?: boolean;
	};
	competenceLabelByKey: Map<string, string>;
	timeReports: any[];
}

const ConsultantSummaryList = ({ consultant, competenceLabelByKey, timeReports }: Props) => {
	const [selectedConsultantToPreview, setSelectedConsultantToPreview] = useState<{
		id: string;
		hasWorked?: boolean;
	} | null>();

	const { isOpen, onOpen, onClose } = useDisclosure();
	const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);

	const jobListData = useMemo((): { url: string; label: string }[] => {
		return makeArrayUniqueByURL(
			timeReports.map((tr) => ({
				label: `${competenceLabelByKey.get(tr.job.competence.service)} - ${tr.job.customer.name}`,
				url: `/uppdrag/${tr.job.id}`,
			})),
		);
	}, [timeReports, competenceLabelByKey]);

	return (
		<VStack height={"100px"} width={"100%"} justify={"center"} alignItems={"flex-start"} px={3} gap={"1px"}>
			<HStack width={"100%"} gap={2}>
				<Text fontWeight={700} size={"16px"} m="0" noOfLines={1}>
					{consultant.firstName}
				</Text>
				<Button
					size={"sm"}
					colorScheme="linkedin"
					variant="link"
					onClick={() => {
						onOpen();
						setSelectedConsultantToPreview({ id: consultant.id, hasWorked: consultant.hasWorked });
					}}
				>
					Visa profil
				</Button>
			</HStack>
			{jobListData.length === 1 ? (
				jobListData.map((jobData) => (
					<Link key={jobData.url} as={RouterLink} to={jobData.url}>
						<Text
							m="0"
							cursor={"pointer"}
							_hover={{ textDecoration: "underline" }}
							color={"#999999"}
							fontSize={"16px"}
							fontWeight={400}
						>
							{jobData.label}
						</Text>
					</Link>
				))
			) : (
				<Text
					m="0"
					cursor={"pointer"}
					_hover={{ textDecoration: "underline" }}
					color={"#999999"}
					fontSize={"16px"}
					fontWeight={400}
					onClick={() => setShowCreateOrderModal(true)}
				>{`Visa alla uppdrag (${jobListData.length})`}</Text>
			)}
			{/* consultant profile drawer */}
			<Drawer
				placement={"right"}
				size={"sm"}
				onClose={() => {
					onClose();
					setSelectedConsultantToPreview(null);
				}}
				isOpen={isOpen}
			>
				<DrawerOverlay />
				<DrawerContent borderLeftRadius={"25px"}>
					<DrawerCloseButton
						onClick={() => {
							onClose();
							setSelectedConsultantToPreview(null);
						}}
					/>
					<DrawerBody p={6}>
						{selectedConsultantToPreview && (
							<ConsultantMiniProfile
								id={selectedConsultantToPreview.id}
								previouslyHired={selectedConsultantToPreview.hasWorked ? true : false}
							/>
						)}
					</DrawerBody>
				</DrawerContent>
			</Drawer>
			{/* job list view modal */}
			<Modal show={showCreateOrderModal} onClose={() => setShowCreateOrderModal(false)}>
				<>
					<Text fontSize={20} fontWeight={600} align={"center"}>
						{`Alla uppdrag, ${consultant.firstName}`}
					</Text>
					<Box maxH={"400px"} overflowY={"scroll"}>
						{jobListData.map(({ url, label }, i) => (
							<Link key={url + i} as={RouterLink} to={url}>
								<HStack
									_hover={{ opacity: 0.7 }}
									cursor={"pointer"}
									bg={"#DFEAF1"}
									my={3}
									p={4}
									justify={"space-between"}
									borderRadius={"full"}
									border={"2px solid #4E7EA7"}
								>
									<Text width={"100%"} textAlign={"center"} m={0} fontSize={"15px"} fontWeight={600}>
										{label}
									</Text>
									<Chevron color={appTheme.colors.brand} />
								</HStack>
							</Link>
						))}
					</Box>
				</>
			</Modal>
		</VStack>
	);
};

export default ConsultantSummaryList;
